import React, { useState, useEffect, useCallback } from 'react';
import {
  Calendar,
  Users,
  CreditCard,
  Ticket,
  Clock,
  ShieldCheck,
  CheckCircle2,
  RefreshCw,
  AlertCircle,
  Eye,
  X
} from 'lucide-react';
import './AdmissionsExecutiveOverview.css';

function formatRupiah(num) {
  const n = Number(num || 0);
  return 'Rp ' + n.toLocaleString('id-ID');
}

function formatDateDisplay(dStr) {
  if (!dStr) return '-';
  try {
    const d = new Date(dStr);
    if (isNaN(d.getTime())) return dStr;
    return d.toLocaleDateString('id-ID', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch (_) {
    return dStr;
  }
}

function cleanTargetTitle(label) {
  if (!label) return '';
  let str = label
    .replace(/^(Tambah|Perbarui|Hapus|Buat)\s+(Periode|Sesi\s+Tes|Jadwal\s+Tes|Kode\s+Promo|Tarif\s+Biaya|Voucher):\s*/i, '')
    .replace(/^(Tambah|Perbarui|Hapus|Buat)\s+(Periode|Sesi\s+Tes|Jadwal\s+Tes|Kode\s+Promo|Tarif\s+Biaya|Voucher):\s*/i, '');
  str = str.replace(/\s*\(([0-9]{4}\/[0-9]{4})\)\s*$/, (match, year) => {
    return str.indexOf(year) !== str.lastIndexOf(year) ? '' : match;
  });
  return str.trim();
}

const LEVEL_META = {
  preschool: { title: 'Preschool (KB / Playgroup)', tag: 'KB', color: '#10b981' },
  kindergarten: { title: 'Kindergarten (TK A & TK B)', tag: 'TK', color: '#06b6d4' },
  primary: { title: 'Primary School (SD Kelas 1 - 6)', tag: 'SD', color: '#3b82f6' },
  secondary: { title: 'Secondary School (SMP Kelas 7 - 9)', tag: 'SMP', color: '#8b5cf6' },
  sma: { title: 'High School (SMA)', tag: 'SMA', color: '#f59e0b' },
};

export default function AdmissionsExecutiveOverview({ apiBase, userRole = 'principal', onNavigateTab }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Detail Modal for Ringkasan PPDB
  const [activeModal, setActiveModal] = useState(null); // { type: 'period' | 'level' | 'test_slot' | 'promo', item: any }

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${apiBase}/admin/admissions/overview`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      const json = await res.json();
      if (res.ok && json.ok) {
        setData(json.data);
      } else {
        setError(json.message || 'Gagal memuat data ringkasan PPDB');
      }
    } catch (_) {
      setError('Koneksi terputus saat memuat data ringkasan PPDB');
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return (
      <div className="aeo-loading-box">
        <RefreshCw size={26} className="aeo-spin" />
        <p>Memuat Data Eksekutif PPDB...</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="aeo-error-box">
        <AlertCircle size={28} />
        <div>
          <h4>Kendala Memuat Data</h4>
          <p>{error || 'Data tidak ditemukan'}</p>
          <button type="button" className="aeo-btn-retry" onClick={loadData}>
            Coba Lagi
          </button>
        </div>
      </div>
    );
  }

  const periods = data.periods || [];
  const activePeriods = periods.filter((p) => !p.is_pending_approval && p.is_active);
  const pendingPeriods = periods.filter((p) => p.is_pending_approval);

  const slots = data.test_slots || [];
  const activeSlots = slots.filter((s) => !s.is_pending_approval && s.is_active);

  const promos = data.promo_codes || [];
  const activePromos = promos.filter((pr) => !pr.is_pending_approval && pr.is_active);

  const levels = data.levels || [];
  const totalApplicants = data.total_applicants || 0;
  const totalTestBooked = activeSlots.reduce((acc, s) => acc + (Number(s.booked_count) || 0), 0);
  const totalPromosUsed = activePromos.reduce((acc, pr) => acc + (Number(pr.used_count) || 0), 0);

  return (
    <div className="aeo-container">
      {/* Executive Header */}
      <div className="aeo-header">
        <div>
          <h2>Ringkasan Eksekutif PPDB &amp; Data Aktif</h2>
          <p>
            Pemantauan status kuota gelombang pendaftaran, kapasitas peserta tes offline, pemakaian kode promo diskon, dan struktur tarif resmi sekolah.
          </p>
        </div>
        <button type="button" className="aeo-btn-refresh" onClick={loadData} title="Perbarui data">
          <RefreshCw size={14} />
          <span>Segarkan Data</span>
        </button>
      </div>

      {/* 4 Stat KPI Cards */}
      <div className="aeo-kpi-grid">
        <div className="aeo-kpi-card aeo-kpi-blue">
          <div className="aeo-kpi-icon-wrap">
            <Calendar size={22} />
          </div>
          <div className="aeo-kpi-info">
            <span className="aeo-kpi-label">Gelombang Pendaftaran</span>
            <div className="aeo-kpi-val">
              {activePeriods.length} <span>Gelombang Aktif</span>
            </div>
            <span className="aeo-kpi-sub">
              {pendingPeriods.length > 0
                ? `${pendingPeriods.length} menunggu otorisasi`
                : 'Semua gelombang terverifikasi'}
            </span>
          </div>
        </div>

        <div className="aeo-kpi-card aeo-kpi-green">
          <div className="aeo-kpi-icon-wrap">
            <Users size={22} />
          </div>
          <div className="aeo-kpi-info">
            <span className="aeo-kpi-label">Total Calon Siswa</span>
            <div className="aeo-kpi-val">
              {totalApplicants} <span>Pendaftar</span>
            </div>
            <span className="aeo-kpi-sub">
              Tersebar di seluruh gelombang &amp; jenjang
            </span>
          </div>
        </div>

        <div className="aeo-kpi-card aeo-kpi-purple">
          <div className="aeo-kpi-icon-wrap">
            <Clock size={22} />
          </div>
          <div className="aeo-kpi-info">
            <span className="aeo-kpi-label">Sesi Tes Masuk Offline</span>
            <div className="aeo-kpi-val">
              {activeSlots.length} <span>Sesi Aktif</span>
            </div>
            <span className="aeo-kpi-sub">
              {totalTestBooked} peserta terdaftar dalam tes
            </span>
          </div>
        </div>

        <div className="aeo-kpi-card aeo-kpi-amber">
          <div className="aeo-kpi-icon-wrap">
            <Ticket size={22} />
          </div>
          <div className="aeo-kpi-info">
            <span className="aeo-kpi-label">Kode Promo &amp; Diskon</span>
            <div className="aeo-kpi-val">
              {activePromos.length} <span>Voucher Aktif</span>
            </div>
            <span className="aeo-kpi-sub">
              {totalPromosUsed} kali diaplikasikan pendaftar
            </span>
          </div>
        </div>
      </div>

      {/* TABLE 1: GELOMBANG PENDAFTARAN PPDB */}
      <div className="aeo-table-card">
        <div className="aeo-card-head">
          <div className="aeo-card-title">
            <Calendar size={17} />
            <h4>Gelombang Pendaftaran PPDB ({periods.length})</h4>
          </div>
        </div>
        <div className="aeo-table-responsive">
          <table className="aeo-clean-table">
            <thead>
              <tr>
                <th>Nama Gelombang / Periode</th>
                <th>Tahun Ajaran</th>
                <th>Rentang Tanggal Buka</th>
                <th>Alokasi Kuota Kursi</th>
                <th>Pendaftar</th>
                <th>Status Gelombang</th>
                <th className="text-right">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {periods.map((p) => {
                const isPending = !!p.is_pending_approval;
                const quotas = p.quotas || {};
                const filled = p.filled || {};
                const totalInWave = p.total_applicants !== undefined ? p.total_applicants : Object.values(filled).reduce((a, b) => a + Number(b), 0);

                return (
                  <tr key={p.id} className={isPending ? 'aeo-row-pending' : ''}>
                    <td>
                      <strong>{cleanTargetTitle(p.period_name)}</strong>
                      {p.notes && <div className="aeo-cell-sub">{p.notes}</div>}
                    </td>
                    <td>
                      <span className="aeo-pill-year">{p.academic_year || '2026/2027'}</span>
                    </td>
                    <td>{formatDateDisplay(p.start_date)} s/d {formatDateDisplay(p.end_date)}</td>
                    <td>
                      <div className="aeo-pills-row">
                        <span className="aeo-pill ps">PS: {quotas.preschool || 25}</span>
                        <span className="aeo-pill kg">KG: {quotas.kindergarten || 30}</span>
                        <span className="aeo-pill pri">PRI: {quotas.primary || 60}</span>
                        <span className="aeo-pill sec">SEC: {quotas.secondary || 45}</span>
                      </div>
                    </td>
                    <td>
                      <strong>{totalInWave}</strong> Siswa
                    </td>
                    <td>
                      {isPending ? (
                        <span className="aeo-status-badge pending">
                          <Clock size={12} />
                          <span>Menunggu Approval</span>
                        </span>
                      ) : p.is_active ? (
                        <span className="aeo-status-badge approved">
                          <CheckCircle2 size={12} />
                          <span>Aktif &amp; Buka</span>
                        </span>
                      ) : (
                        <span className="aeo-status-badge closed">Tutup</span>
                      )}
                    </td>
                    <td className="text-right">
                      <button
                        type="button"
                        className="aeo-btn-detail"
                        onClick={() => setActiveModal({ type: 'period', item: p })}
                      >
                        <Eye size={13} />
                        <span>Lihat Detail</span>
                      </button>
                    </td>
                  </tr>
                );
              })}
              {periods.length === 0 && (
                <tr>
                  <td colSpan="7" className="aeo-empty-text">Belum ada periode pendaftaran</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* TABLE 2: STRUKTUR TARIF BIAYA JENJANG */}
      <div className="aeo-table-card">
        <div className="aeo-card-head">
          <div className="aeo-card-title">
            <CreditCard size={17} />
            <h4>Tarif Biaya Masuk &amp; SPP Resmi Sekolah ({levels.length} Jenjang)</h4>
          </div>
        </div>
        <div className="aeo-table-responsive">
          <table className="aeo-clean-table">
            <thead>
              <tr>
                <th>Jenjang Pendidikan</th>
                <th>Kategori</th>
                <th>Uang Pangkal (Development Fee)</th>
                <th>SPP Bulanan</th>
                <th>Paket Buku &amp; Masuk</th>
                <th>Rekening Yayasan</th>
                <th>Status Tarif</th>
                <th className="text-right">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {levels.map((lvl) => {
                const meta = LEVEL_META[lvl.level_code] || {
                  title: lvl.level_name,
                  tag: lvl.tag_code || 'GEN',
                  color: '#3b82f6',
                };
                const hasPendingFee = !!lvl.pending_fee_payload || lvl.fee_approval_status === 'pending_principal';

                return (
                  <tr key={lvl.level_code} className={hasPendingFee ? 'aeo-row-pending' : ''}>
                    <td>
                      <div className="aeo-level-name-row">
                        <span className="aeo-level-tag-mini" style={{ background: meta.color }}>
                          {meta.tag}
                        </span>
                        <strong>{lvl.level_name}</strong>
                      </div>
                    </td>
                    <td>
                      <span className="aeo-pill-cat">
                        {lvl.fee_category === 'early_years' ? 'Early Years' : 'School Years'}
                      </span>
                    </td>
                    <td>
                      <strong className="aeo-mono-val">{formatRupiah(lvl.development_fee)}</strong>
                    </td>
                    <td>
                      <strong className="aeo-mono-val highlight-blue">{formatRupiah(lvl.tuition_fee)}</strong>
                    </td>
                    <td>
                      <div>Buku: {formatRupiah(lvl.books_activity_fee)}</div>
                      <div className="aeo-cell-sub">{lvl.admission_fee > 0 ? `Masuk: ${formatRupiah(lvl.admission_fee)}` : 'Bebas Biaya Masuk'}</div>
                    </td>
                    <td>
                      <div>{lvl.bank_name || 'BCA'}</div>
                      <div className="aeo-cell-sub">{lvl.bank_account_number || '548-098-7788'}</div>
                    </td>
                    <td>
                      {hasPendingFee ? (
                        <span className="aeo-status-badge pending">
                          <Clock size={12} />
                          <span>Ada Usulan Revisi</span>
                        </span>
                      ) : (
                        <span className="aeo-status-badge approved">
                          <ShieldCheck size={12} />
                          <span>Tarif Resmi Aktif</span>
                        </span>
                      )}
                    </td>
                    <td className="text-right">
                      <button
                        type="button"
                        className="aeo-btn-detail"
                        onClick={() => setActiveModal({ type: 'level', item: lvl })}
                      >
                        <Eye size={13} />
                        <span>Lihat Detail</span>
                      </button>
                    </td>
                  </tr>
                );
              })}
              {levels.length === 0 && (
                <tr>
                  <td colSpan="8" className="aeo-empty-text">Belum ada data tarif jenjang</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* TABLES 3 & 4: 2-COLUMN GRID FOR TEST SLOTS & PROMOS */}
      <div className="aeo-two-col-grid">
        {/* Jadwal Tes Aktif */}
        <div className="aeo-table-card">
          <div className="aeo-card-head">
            <div className="aeo-card-title">
              <Clock size={17} />
              <h4>Jadwal Sesi Tes Offline ({slots.length})</h4>
            </div>
          </div>
          <div className="aeo-table-responsive">
            <table className="aeo-clean-table">
              <thead>
                <tr>
                  <th>Sesi Tes</th>
                  <th>Tanggal &amp; Waktu</th>
                  <th>Kampus &amp; Ruangan</th>
                  <th>Peserta</th>
                  <th className="text-right">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {slots.slice(0, 6).map((s) => (
                  <tr key={s.id}>
                    <td>
                      <strong>{cleanTargetTitle(s.session_name)}</strong>
                    </td>
                    <td>
                      <div>{formatDateDisplay(s.test_date)}</div>
                      <div className="aeo-cell-sub">{s.test_time}</div>
                    </td>
                    <td>
                      <div>{s.campus_location}</div>
                      <div className="aeo-cell-sub">{s.room_name || 'Ruang Assessment'}</div>
                    </td>
                    <td>
                      <span className="aeo-booked-pill">
                        <strong>{s.booked_count || 0}</strong> / {s.slot_quota}
                      </span>
                    </td>
                    <td className="text-right">
                      <button
                        type="button"
                        className="aeo-btn-detail"
                        onClick={() => setActiveModal({ type: 'test_slot', item: s })}
                      >
                        <Eye size={13} />
                        <span>Detail</span>
                      </button>
                    </td>
                  </tr>
                ))}
                {slots.length === 0 && (
                  <tr>
                    <td colSpan="5" className="aeo-empty-text">Belum ada sesi tes</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Kode Promo Aktif */}
        <div className="aeo-table-card">
          <div className="aeo-card-head">
            <div className="aeo-card-title">
              <Ticket size={17} />
              <h4>Kode Promo &amp; Diskon ({promos.length})</h4>
            </div>
          </div>
          <div className="aeo-table-responsive">
            <table className="aeo-clean-table">
              <thead>
                <tr>
                  <th>Kode Voucher</th>
                  <th>Potongan Diskon</th>
                  <th>Masa Berlaku</th>
                  <th>Terpakai</th>
                  <th className="text-right">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {promos.slice(0, 6).map((pr) => (
                  <tr key={pr.id}>
                    <td>
                      <span className="aeo-promo-chip">{pr.promo_code}</span>
                      <div className="aeo-cell-sub">{pr.promo_title}</div>
                    </td>
                    <td>
                      <strong style={{ color: '#059669' }}>
                        {pr.discount_type === 'percentage'
                          ? `${pr.discount_value}% DF`
                          : formatRupiah(pr.discount_value)}
                      </strong>
                    </td>
                    <td>{formatDateDisplay(pr.valid_until)}</td>
                    <td>
                      <strong>{pr.used_count || 0}</strong> / {pr.max_quota}
                    </td>
                    <td className="text-right">
                      <button
                        type="button"
                        className="aeo-btn-detail"
                        onClick={() => setActiveModal({ type: 'promo', item: pr })}
                      >
                        <Eye size={13} />
                        <span>Detail</span>
                      </button>
                    </td>
                  </tr>
                ))}
                {promos.length === 0 && (
                  <tr>
                    <td colSpan="5" className="aeo-empty-text">Belum ada kode promo</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* DETAIL MODAL POPUP FOR RINGKASAN PPDB */}
      {activeModal && (
        <div className="aeo-modal-overlay" onClick={() => setActiveModal(null)}>
          <div className="aeo-modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="aeo-modal-head">
              <h3>
                {activeModal.type === 'period'
                  ? `Rincian Gelombang: ${cleanTargetTitle(activeModal.item.period_name)}`
                  : activeModal.type === 'level'
                  ? `Rincian Biaya: ${activeModal.item.level_name}`
                  : activeModal.type === 'test_slot'
                  ? `Rincian Sesi Tes: ${cleanTargetTitle(activeModal.item.session_name)}`
                  : `Rincian Voucher: ${activeModal.item.promo_code}`}
              </h3>
              <button type="button" className="aeo-modal-close" onClick={() => setActiveModal(null)}>
                <X size={18} />
              </button>
            </div>

            <div className="aeo-modal-body">
              {/* PERIOD MODAL */}
              {activeModal.type === 'period' && (() => {
                const p = activeModal.item;
                const quotas = p.quotas || {};
                return (
                  <div className="aeo-modal-grid">
                    <div className="aeo-modal-tile">
                      <span className="aeo-tile-lbl">Tahun Ajaran</span>
                      <strong>{p.academic_year || '2026/2027'}</strong>
                    </div>
                    <div className="aeo-modal-tile">
                      <span className="aeo-tile-lbl">Durasi Buka</span>
                      <strong>{formatDateDisplay(p.start_date)} s/d {formatDateDisplay(p.end_date)}</strong>
                    </div>
                    <div className="aeo-modal-tile">
                      <span className="aeo-tile-lbl">Status</span>
                      <strong style={{ color: p.is_active ? '#059669' : '#64748b' }}>
                        {p.is_active ? 'Aktif Menerima Pendaftaran' : 'Tutup'}
                      </strong>
                    </div>
                    <div className="aeo-modal-tile aeo-full-col">
                      <span className="aeo-tile-lbl">Alokasi Kuota Kursi</span>
                      <div className="aeo-pills-row" style={{ marginTop: 6 }}>
                        <span className="aeo-pill ps">Preschool: {quotas.preschool || 25} Kursi</span>
                        <span className="aeo-pill kg">Kindergarten: {quotas.kindergarten || 30} Kursi</span>
                        <span className="aeo-pill pri">Primary: {quotas.primary || 60} Kursi</span>
                        <span className="aeo-pill sec">Secondary: {quotas.secondary || 45} Kursi</span>
                      </div>
                    </div>
                    {p.notes && (
                      <div className="aeo-modal-tile aeo-full-col">
                        <span className="aeo-tile-lbl">Catatan Internal</span>
                        <p style={{ margin: '4px 0 0', color: '#475569', fontStyle: 'italic' }}>"{p.notes}"</p>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* LEVEL MODAL */}
              {activeModal.type === 'level' && (() => {
                const lvl = activeModal.item;
                return (
                  <div className="aeo-modal-grid">
                    <div className="aeo-modal-tile">
                      <span className="aeo-tile-lbl">SPP Bulanan</span>
                      <strong className="highlight-blue">{formatRupiah(lvl.tuition_fee)}</strong>
                    </div>
                    <div className="aeo-modal-tile">
                      <span className="aeo-tile-lbl">Total Uang Pangkal</span>
                      <strong>{formatRupiah(lvl.development_fee)}</strong>
                    </div>
                    {lvl.df_1_amount > 0 && (
                      <div className="aeo-modal-tile">
                        <span className="aeo-tile-lbl">Termin 1 (Saat Daftar Ulang)</span>
                        <strong>{formatRupiah(lvl.df_1_amount)}</strong>
                      </div>
                    )}
                    <div className="aeo-modal-tile">
                      <span className="aeo-tile-lbl">Paket Buku &amp; Kegiatan</span>
                      <strong>{formatRupiah(lvl.books_activity_fee)}</strong>
                    </div>
                    <div className="aeo-modal-tile">
                      <span className="aeo-tile-lbl">Biaya Formulir Masuk</span>
                      <strong>{lvl.admission_fee > 0 ? formatRupiah(lvl.admission_fee) : 'Bebas Biaya'}</strong>
                    </div>
                    <div className="aeo-modal-tile">
                      <span className="aeo-tile-lbl">Rekening Bank Sekolah</span>
                      <strong>{lvl.bank_name || 'BCA'} {lvl.bank_account_number || '548-098-7788'}</strong>
                    </div>
                  </div>
                );
              })()}

              {/* TEST SLOT MODAL */}
              {activeModal.type === 'test_slot' && (() => {
                const s = activeModal.item;
                return (
                  <div className="aeo-modal-grid">
                    <div className="aeo-modal-tile">
                      <span className="aeo-tile-lbl">Tanggal Pelaksanaan</span>
                      <strong>{formatDateDisplay(s.test_date)}</strong>
                    </div>
                    <div className="aeo-modal-tile">
                      <span className="aeo-tile-lbl">Waktu Sesi</span>
                      <strong>{s.test_time || '09:00 - 11:00 WIB'}</strong>
                    </div>
                    <div className="aeo-modal-tile">
                      <span className="aeo-tile-lbl">Lokasi Kampus</span>
                      <strong>{s.campus_location}</strong>
                    </div>
                    <div className="aeo-modal-tile">
                      <span className="aeo-tile-lbl">Ruangan</span>
                      <strong>{s.room_name || 'Ruang Assessment'}</strong>
                    </div>
                    <div className="aeo-modal-tile">
                      <span className="aeo-tile-lbl">Kapasitas &amp; Terisi</span>
                      <strong>{s.booked_count || 0} / {s.slot_quota} Peserta</strong>
                    </div>
                    <div className="aeo-modal-tile">
                      <span className="aeo-tile-lbl">Jenjang</span>
                      <strong>{String(s.level_code || 'SEMUA JENJANG').toUpperCase()}</strong>
                    </div>
                  </div>
                );
              })()}

              {/* PROMO MODAL */}
              {activeModal.type === 'promo' && (() => {
                const pr = activeModal.item;
                return (
                  <div className="aeo-modal-grid">
                    <div className="aeo-modal-tile">
                      <span className="aeo-tile-lbl">Kode Voucher</span>
                      <strong style={{ color: '#b45309', fontFamily: 'monospace', fontSize: '1.1rem' }}>{pr.promo_code}</strong>
                    </div>
                    <div className="aeo-modal-tile">
                      <span className="aeo-tile-lbl">Potongan Diskon</span>
                      <strong style={{ color: '#059669' }}>
                        {pr.discount_type === 'percentage' ? `${pr.discount_value}% DF` : formatRupiah(pr.discount_value)}
                      </strong>
                    </div>
                    <div className="aeo-modal-tile">
                      <span className="aeo-tile-lbl">Nama Promo</span>
                      <strong>{pr.promo_title}</strong>
                    </div>
                    <div className="aeo-modal-tile">
                      <span className="aeo-tile-lbl">Masa Berlaku</span>
                      <strong>{formatDateDisplay(pr.valid_until)}</strong>
                    </div>
                    <div className="aeo-modal-tile">
                      <span className="aeo-tile-lbl">Kuota Penggunaan</span>
                      <strong>{pr.used_count || 0} / {pr.max_quota} Siswa</strong>
                    </div>
                  </div>
                );
              })()}
            </div>

            <div className="aeo-modal-foot">
              <button type="button" className="aeo-btn-modal-close" onClick={() => setActiveModal(null)}>
                Tutup
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
